import React, { useRef, useEffect } from 'react';
import { NavBar } from '../NavBar';
import type { UserMonitorConfig } from '../../../types/UserMonitorConfig';
import styles from './AppShell.module.css';

interface AppShellProps {
  /** Optional sidebar content */
  sidebar?: React.ReactNode;
  /**
   * When provided the layout becomes a full-viewport flex row.
   * The NavBar sits only above the left 2/3; the right 1/3 spans the full height.
   */
  rightPanel?: React.ReactNode;
  /**
   * Rendered as a fixed-height section at the bottom of the left pane.
   * Takes up the bottom half, pushing content upward with a slide-up animation.
   */
  detailOverlay?: React.ReactNode;
  children?: React.ReactNode;
  onMyMonitorClick?: () => void;
  userConfig: UserMonitorConfig;
  isConfigured: boolean;
}

export function AppShell({ sidebar, rightPanel, detailOverlay, children, onMyMonitorClick, userConfig, isConfigured }: AppShellProps) {
  const hasDetail = Boolean(detailOverlay);

  // When the detail panel opens/closes, maintain the "distance from the bottom"
  // invariant across the CSS flex transition. We snapshot fromBottom at the moment
  // hasDetail changes, then a ResizeObserver fires on every animation frame while
  // clientHeight changes, continuously restoring scrollTop so the same content
  // stays visible at the bottom — even when already scrolled to the end.
  const catalogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = catalogRef.current;
    if (!el) return;

    // Snapshot: how many px of content are below the current visible bottom?
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    const ro = new ResizeObserver(() => {
      // Re-anchor on every frame that clientHeight changes during the transition.
      el.scrollTop = el.scrollHeight - el.clientHeight - fromBottom;
    });
    ro.observe(el);

    // Disconnect after the transition duration (350 ms) + a small buffer.
    const id = window.setTimeout(() => ro.disconnect(), 400);
    return () => { ro.disconnect(); window.clearTimeout(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDetail]);

  /* ── Split layout: [NavBar + content | right panel] ── */
  if (rightPanel) {
    return (
      <div className={styles.splitRoot}>
        {/* Left 2/3 — NavBar + scrollable content + detail panel */}
        <div className={styles.leftPane}>
          <NavBar userConfig={userConfig} isConfigured={isConfigured} onMyMonitorClick={onMyMonitorClick} />
          <div className={styles.contentViewport}>
            <div className={styles.leftContent} ref={catalogRef}>
              {sidebar && <aside className={styles.sidebar}>{sidebar}</aside>}
              <main className={styles.main}>{children}</main>
            </div>
            {/* Detail section — pushes catalog upward when open */}
            <div className={`${styles.detailSection} ${hasDetail ? styles.detailSectionOpen : styles.detailSectionClosed}`}>
              {detailOverlay}
            </div>
          </div>
        </div>

        {/* Right 1/3 — full viewport height, no NavBar */}
        <div className={styles.rightPane}>
          {rightPanel}
        </div>
      </div>
    );
  }

  /* ── Default single-column layout ── */
  return (
    <div className={styles.root}>
      <NavBar userConfig={userConfig} isConfigured={isConfigured} onMyMonitorClick={onMyMonitorClick} />
      <div className={styles.body}>
        {sidebar && <aside className={styles.sidebar}>{sidebar}</aside>}
        <main className={styles.main}>{children}</main>
      </div>
    </div>
  );
}
